/**
 * Topic Detail Dialog Component
 * Shows topic content, assessment, presentation slides, and review options
 */

import { useState } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { ScrollArea } from '../ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import {
    CheckCircle,
    BookOpen,
    Target,
    Lightbulb,
    ClipboardCheck,
    RefreshCw
} from 'lucide-react';

// Sub-components for each tab
const ContentTab = ({ topic }) => (
    <div className="space-y-4">
        {/* Learning Objectives */}
        <div className="p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
            <h4 className="font-medium flex items-center gap-2 mb-2">
                <Target className="h-4 w-4 text-emerald-500" />
                Learning Objectives
            </h4>
            <ul className="space-y-1">
                {topic.learning_objectives?.map((obj, idx) => (
                    <li key={idx} className="text-sm flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                        {obj}
                    </li>
                ))}
            </ul>
        </div>
        
        {/* Introduction */}
        {topic.introduction && (
            <div className="p-4 rounded-lg bg-muted/50">
                <h4 className="font-medium mb-2">Introduction</h4>
                <p className="text-sm">{topic.introduction}</p>
            </div>
        )}
        
        {/* Sections */}
        {topic.sections?.map((section, idx) => (
            <div key={idx} className="border rounded-lg p-4 space-y-3">
                <h4 className="font-semibold text-lg flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm">
                        {idx + 1}
                    </span>
                    {section.title}
                </h4>
                
                <p className="text-sm whitespace-pre-wrap">{section.content}</p>
                
                {/* Examples */}
                {section.examples?.length > 0 && (
                    <div className="space-y-2 mt-4">
                        <h5 className="text-sm font-medium flex items-center gap-2">
                            <Lightbulb className="h-4 w-4 text-amber-500" />
                            Examples
                        </h5>
                        {section.examples.map((ex, exIdx) => (
                            <div key={exIdx} className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                                <p className="font-medium text-sm">{ex.title}</p>
                                {ex.scenario && <p className="text-sm mt-1">{ex.scenario}</p>}
                                {ex.walkthrough && (
                                    <p className="text-sm mt-2 text-muted-foreground">{ex.walkthrough}</p>
                                )}
                                {ex.key_lesson && (
                                    <p className="text-sm mt-2 font-medium text-amber-600">
                                        Key Lesson: {ex.key_lesson}
                                    </p>
                                )}
                            </div>
                        ))}
                    </div>
                )}
                
                {/* Key Points */}
                {section.key_points?.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3">
                        {section.key_points.map((point, pIdx) => (
                            <Badge key={pIdx} variant="outline" className="text-xs">
                                {point}
                            </Badge>
                        ))}
                    </div>
                )}
            </div>
        ))}
        
        {/* Practical Exercises */}
        {topic.practical_exercises?.length > 0 && (
            <div className="space-y-3">
                <h4 className="font-medium flex items-center gap-2">
                    <ClipboardCheck className="h-4 w-4 text-blue-500" />
                    Practical Exercises
                </h4>
                {topic.practical_exercises.map((ex, idx) => (
                    <div key={idx} className="p-4 rounded-lg bg-blue-500/10 border border-blue-500/20">
                        <h5 className="font-medium">{ex.title}</h5>
                        <p className="text-sm mt-1">{ex.description}</p>
                        {ex.instructions && (
                            <ol className="list-decimal list-inside mt-2 text-sm space-y-1">
                                {ex.instructions.map((inst, iIdx) => (
                                    <li key={iIdx}>{inst}</li>
                                ))}
                            </ol>
                        )}
                    </div>
                ))}
            </div>
        )}
    </div>
);

const AssessmentTab = ({ topic }) => (
    <div className="space-y-4">
        <div className="p-4 rounded-lg bg-muted/50 mb-4">
            <p className="text-sm">
                <strong>Passing Score:</strong> {topic.assessment?.passing_score || 70}%
            </p>
            <p className="text-sm text-muted-foreground">
                {topic.assessment?.questions?.length || 0} questions
            </p>
        </div>
        
        {topic.assessment?.questions?.map((q, idx) => (
            <div key={idx} className="p-4 rounded-lg border">
                <div className="flex items-start justify-between mb-2">
                    <span className="font-medium">Q{idx + 1}. {q.question}</span>
                    <Badge variant="outline">{q.type}</Badge>
                </div>
                
                {q.scenario && (
                    <p className="text-sm text-muted-foreground mb-2 italic">
                        Scenario: {q.scenario}
                    </p>
                )}
                
                {q.options && (
                    <div className="space-y-1 mt-2">
                        {q.options.map((opt, oIdx) => (
                            <div 
                                key={oIdx} 
                                className={`text-sm p-2 rounded ${
                                    opt === q.correct_answer 
                                        ? 'bg-emerald-500/10 text-emerald-600 font-medium' 
                                        : 'bg-muted/50'
                                }`}
                            >
                                {String.fromCharCode(65 + oIdx)}. {opt}
                                {opt === q.correct_answer && ' ✓'}
                            </div>
                        ))}
                    </div>
                )}
                
                {q.type === 'true_false' && (
                    <p className="text-sm mt-2">
                        <strong>Answer:</strong> {q.correct_answer ? 'True' : 'False'}
                    </p>
                )}
                
                {q.explanation && (
                    <p className="text-sm text-muted-foreground mt-2 p-2 bg-muted/30 rounded">
                        <strong>Explanation:</strong> {q.explanation}
                    </p>
                )}
            </div>
        ))}
    </div>
);

const PresentationTab = ({ topic }) => (
    <div className="space-y-4">
        <p className="text-sm text-muted-foreground mb-4">
            {topic.presentation?.slides?.length || 0} slides generated
        </p>
        
        <div className="grid gap-3">
            {topic.presentation?.slides?.map((slide, idx) => (
                <div key={idx} className="p-4 rounded-lg border bg-card">
                    <div className="flex items-center gap-2 mb-2">
                        <Badge variant="outline">Slide {idx + 1}</Badge>
                        <Badge variant="secondary">{slide.type}</Badge>
                    </div>
                    <h5 className="font-semibold">{slide.title}</h5>
                    {slide.subtitle && <p className="text-sm text-muted-foreground">{slide.subtitle}</p>}
                    {slide.bullet_points && (
                        <ul className="mt-2 space-y-1">
                            {slide.bullet_points.map((point, pIdx) => (
                                <li key={pIdx} className="text-sm">• {point}</li>
                            ))}
                        </ul>
                    )}
                    {slide.speaker_notes && (
                        <p className="text-xs text-muted-foreground mt-2 italic">
                            Notes: {slide.speaker_notes}
                        </p>
                    )}
                </div>
            ))}
        </div>
    </div>
);

const ReviewTab = ({ topic, onSubmitFeedback, onApprove }) => {
    const [feedbackText, setFeedbackText] = useState('');
    const [regenerateSections, setRegenerateSections] = useState([]);

    const handleSubmit = () => {
        onSubmitFeedback(feedbackText, regenerateSections);
        setFeedbackText('');
        setRegenerateSections([]);
    };

    return (
        <div className="space-y-4">
            <div className="p-4 rounded-lg border-2 border-dashed">
                <h4 className="font-medium mb-3">Provide Feedback</h4>
                
                <Textarea
                    value={feedbackText}
                    onChange={(e) => setFeedbackText(e.target.value)}
                    placeholder="Enter your feedback or suggestions for improvement..."
                    rows={3}
                />
                
                <div className="mt-3">
                    <Label className="text-sm">Regenerate Sections (Optional)</Label>
                    <div className="flex flex-wrap gap-2 mt-2">
                        {['sections', 'assessment', 'presentation', 'exercises'].map(section => (
                            <Badge
                                key={section}
                                variant={regenerateSections.includes(section) ? 'default' : 'outline'}
                                className="cursor-pointer capitalize"
                                onClick={() => {
                                    setRegenerateSections(prev => 
                                        prev.includes(section) 
                                            ? prev.filter(s => s !== section)
                                            : [...prev, section]
                                    );
                                }}
                            >
                                {section}
                            </Badge>
                        ))}
                    </div>
                </div>
                
                <div className="flex gap-2 mt-4">
                    <Button onClick={handleSubmit} variant="outline" className="flex-1">
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Submit Feedback
                    </Button>
                    
                    {topic.status !== 'approved' && (
                        <Button onClick={onApprove} className="flex-1">
                            <CheckCircle className="h-4 w-4 mr-2" />
                            Approve Topic
                        </Button>
                    )}
                </div>
            </div>
            
            {topic.status === 'approved' && (
                <div className="p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/30 flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-emerald-500" />
                    <span className="font-medium">This topic has been approved</span>
                </div>
            )}
        </div>
    );
};

const TopicDetailDialog = ({
    open,
    onOpenChange,
    topicData,
    onSubmitFeedback,
    onApprove
}) => {
    if (!topicData) return null;

    const topic = topicData.topic;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-4xl max-h-[90vh]">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <BookOpen className="h-5 w-5 text-primary" />
                        {topic?.name}
                    </DialogTitle>
                </DialogHeader>
                
                <ScrollArea className="h-[70vh] pr-4">
                    <Tabs defaultValue="content" className="w-full">
                        <TabsList className="grid w-full grid-cols-4">
                            <TabsTrigger value="content">Content</TabsTrigger>
                            <TabsTrigger value="assessment">Assessment</TabsTrigger>
                            <TabsTrigger value="presentation">Slides</TabsTrigger>
                            <TabsTrigger value="review">Review</TabsTrigger>
                        </TabsList>
                        
                        <TabsContent value="content" className="mt-4">
                            <ContentTab topic={topic} />
                        </TabsContent>
                        
                        <TabsContent value="assessment" className="mt-4">
                            <AssessmentTab topic={topic} />
                        </TabsContent>
                        
                        <TabsContent value="presentation" className="mt-4">
                            <PresentationTab topic={topic} />
                        </TabsContent>
                        
                        <TabsContent value="review" className="mt-4">
                            <ReviewTab 
                                topic={topic} 
                                onSubmitFeedback={onSubmitFeedback}
                                onApprove={onApprove}
                            />
                        </TabsContent>
                    </Tabs>
                </ScrollArea>
            </DialogContent>
        </Dialog>
    );
};

export default TopicDetailDialog;
